import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ManageAccessDraftState,
  ManageAccessState,
  ManageAccessStep,
} from '@/types/manage-access.types';
import type { SeatSummary } from '@/types/create-six.types';

const initialDraft: ManageAccessDraftState = {
  ownerIds: [],
  internalParticipantIds: [],
  externalParticipantIds: [],
  newExternalUsers: [],
};

const initialState: ManageAccessState = {
  step: 'Owners',
  isSubmitting: false,
  sixId: null,
  hasPendingExternalManualEntries: false,
  draft: initialDraft,
  internalUsers: [],
  externalUsers: [],
  externalSeats: null,
};

type DraftKey = keyof ManageAccessDraftState;

export const manageAccessSlice = createSlice({
  name: 'manageAccess',
  initialState,
  reducers: {
    resetManageAccessState: () => initialState,

    setManageAccessStep: (state, action: PayloadAction<ManageAccessStep>) => {
      state.step = action.payload;
    },

    setManageAccessSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    setManageAccessSixId: (state, action: PayloadAction<string | null>) => {
      state.sixId = action.payload;
    },

    setManageAccessHasPendingExternalManualEntries: (state, action: PayloadAction<boolean>) => {
      state.hasPendingExternalManualEntries = action.payload;
    },

    setManageAccessInternalUsers: (
      state,
      action: PayloadAction<Array<{ id: string; name: string; email: string; title?: string }>>
    ) => {
      state.internalUsers = action.payload;
    },

    setManageAccessExternalUsers: (
      state,
      action: PayloadAction<
        Array<{ id: string; name: string; email: string; deptOrg: string; title?: string }>
      >
    ) => {
      state.externalUsers = action.payload;
    },

    setManageAccessExternalSeats: (state, action: PayloadAction<SeatSummary | null>) => {
      state.externalSeats = action.payload;
    },

    setManageAccessDraftField: <Key extends DraftKey>(
      state: ManageAccessState,
      action: PayloadAction<{ key: Key; value: ManageAccessDraftState[Key] }>
    ) => {
      state.draft = { ...state.draft, [action.payload.key]: action.payload.value };
    },

    hydrateManageAccessDraft: (state, action: PayloadAction<ManageAccessDraftState>) => {
      state.draft = action.payload;
    },
  },
});

export const {
  resetManageAccessState,
  setManageAccessStep,
  setManageAccessSubmitting,
  setManageAccessSixId,
  setManageAccessHasPendingExternalManualEntries,
  setManageAccessInternalUsers,
  setManageAccessExternalUsers,
  setManageAccessExternalSeats,
  setManageAccessDraftField,
  hydrateManageAccessDraft,
} = manageAccessSlice.actions;

export default manageAccessSlice.reducer;
